import React, { useEffect, useRef } from "react";
import {
  Bell,
  Building2,
  Calendar,
  FileText,
  ListTodo,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  WifiOff,
  type LucideIcon,
} from "lucide-react";
import type { AppNotification, ConnectionStatus } from "./types";
import type { LoadState } from "./notificationsStore";

export interface NotificationsDropdownLabels {
  title?: string;
  empty?: string;
  markAllRead?: string;
  loading?: string;
  error?: string;
  retry?: string;
  reconnecting?: string;
  loadMore?: string;
}

export interface NotificationsDropdownProps {
  open: boolean;
  items: AppNotification[];
  anchorRef: React.RefObject<HTMLElement | null>;
  onSelect: (item: AppNotification) => void;
  onClose: () => void;
  labels?: NotificationsDropdownLabels;
  unread?: number;
  loadState?: LoadState;
  connection?: ConnectionStatus;
  hasMore?: boolean;
  loadingMore?: boolean;
  onMarkAllRead?: () => void;
  onLoadMore?: () => void;
  onRetry?: () => void;
  /** Constrain the popover to a compact sidebar instead of the desktop width. */
  compact?: boolean;
}

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  chat: MessageSquare,
  calls: Phone,
  email: Mail,
  homework: Building2,
  docs: FileText,
  calendar: Calendar,
  tasks: ListTodo,
};

export const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({
  open,
  items,
  anchorRef,
  onSelect,
  onClose,
  labels,
  unread = 0,
  loadState = "ready",
  connection = "connected",
  hasMore = false,
  loadingMore = false,
  onMarkAllRead,
  onLoadMore,
  onRetry,
  compact = false,
}) => {
  const {
    title = "Notifications",
    empty = "You're all caught up.",
    markAllRead = "Mark all as read",
    loading = "Loading notifications…",
    error = "Couldn't load notifications.",
    retry = "Try again",
    reconnecting = "Reconnecting… new notifications may be delayed.",
    loadMore = "Show older",
  } = labels ?? {};
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    }

    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      const inDropdown = dropdownRef.current?.contains(target) ?? false;
      const inAnchor = anchorRef.current?.contains(target) ?? false;
      if (!inDropdown && !inAnchor) onClose();
    }

    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open, anchorRef, onClose]);

  if (!open) return null;

  const hasUnread = unread > 0 || items.some((i) => !i.read_at);

  return (
    <div
      ref={dropdownRef}
      role="menu"
      aria-label={title}
      aria-busy={loadState === "loading" || undefined}
      className={`absolute top-full mt-2 max-h-[420px] overflow-y-auto rounded-2xl border border-border bg-background shadow-xl z-50 ${
        compact ? "inset-x-0 w-auto" : "ltr:right-0 rtl:left-0 w-80"
      }`}
    >
      <header className="flex items-center gap-2 px-4 pt-3 pb-1">
        <span className="flex-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
        {onMarkAllRead && hasUnread && (
          <button
            type="button"
            onClick={onMarkAllRead}
            className="text-xs font-semibold text-foreground/70 hover:text-foreground rounded-lg px-1.5 py-0.5 hover:bg-foreground/5 transition-colors"
          >
            {markAllRead}
          </button>
        )}
      </header>

      {connection === "reconnecting" && (
        <div role="status" className="mx-4 mb-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <WifiOff className="size-3 shrink-0" aria-hidden />
          <span>{reconnecting}</span>
        </div>
      )}

      {loadState === "loading" && items.length === 0 ? (
        <div role="status" className="px-4 pb-4 pt-1 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          <span>{loading}</span>
        </div>
      ) : loadState === "error" && items.length === 0 ? (
        <div role="alert" className="px-4 pb-4 pt-1 text-sm text-muted-foreground">
          <p>{error}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 text-xs font-semibold text-foreground underline underline-offset-2 hover:opacity-80"
            >
              {retry}
            </button>
          )}
        </div>
      ) : items.length === 0 ? (
        <div className="px-4 pb-4 pt-1 text-sm text-muted-foreground">{empty}</div>
      ) : (
        <ul className="pb-2">
          {items.map((item) => {
            const Icon = CATEGORY_ICONS[item.category] ?? Bell;
            const isUnread = !item.read_at;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  role="menuitem"
                  data-unread={isUnread || undefined}
                  onClick={() => onSelect(item)}
                  className={`w-full flex items-start gap-2.5 px-4 py-2.5 text-start text-sm text-foreground hover:bg-foreground/5 transition-colors ${
                    isUnread ? "bg-primary/5" : ""
                  }`}
                >
                  <span className="mt-0.5 shrink-0 size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="size-3.5" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate ${isUnread ? "font-semibold" : "font-medium"}`}>{item.title}</span>
                    {item.body && (
                      <span className="block text-xs text-muted-foreground line-clamp-2 break-words">{item.body}</span>
                    )}
                    <time dateTime={item.created_at} className="block mt-0.5 text-[11px] text-muted-foreground">
                      {relativeTime(item.created_at)}
                    </time>
                  </span>
                  {isUnread && (
                    <span
                      data-testid="notification-unread-marker"
                      aria-hidden
                      className="mt-1.5 shrink-0 size-2 rounded-full bg-primary"
                    />
                  )}
                </button>
              </li>
            );
          })}
          {hasMore && onLoadMore && (
            <li className="px-4 pt-1">
              <button
                type="button"
                onClick={onLoadMore}
                disabled={loadingMore}
                className="w-full rounded-xl py-1.5 text-xs font-semibold text-foreground/70 hover:text-foreground hover:bg-foreground/5 disabled:opacity-60 transition-colors flex items-center justify-center gap-1.5"
              >
                {loadingMore && <Loader2 className="size-3 animate-spin" aria-hidden />}
                {loadMore}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

/** "5 min. ago" in the page's language — the browser's own wording, so there is nothing to translate. */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const lang = (typeof document !== "undefined" && document.documentElement.lang) || undefined;
  const seconds = Math.round((t - now) / 1000);
  const abs = Math.abs(seconds);
  try {
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto", style: "short" });
    if (abs < 45) return rtf.format(0, "second");
    if (abs < 3_600) return rtf.format(Math.round(seconds / 60), "minute");
    if (abs < 86_400) return rtf.format(Math.round(seconds / 3_600), "hour");
    if (abs < 7 * 86_400) return rtf.format(Math.round(seconds / 86_400), "day");
    return new Date(t).toLocaleDateString(lang, { month: "short", day: "numeric" });
  } catch {
    return new Date(t).toLocaleString();
  }
}
